<template>
  <UiDialog :open="open" :title="item && item.id ? 'Modifier l\'article' : 'Nouvel article d\'inventaire'" xl @close="$emit('close')">
    <div class="space-y-4">
      <div class="grid grid-cols-1 gap-3 md:grid-cols-2">
        <div class="md:col-span-2">
          <label class="mb-1 block text-xs font-medium text-muted-foreground">Nom du produit *</label>
          <UiInput v-model="form.name" placeholder="Ex. Ordinateur portable Dell XPS" />
        </div>

        <div>
          <label class="mb-1 block text-xs font-medium text-muted-foreground">Date</label>
          <input type="date" v-model="form.date"
                 class="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" />
        </div>
        <div>
          <label class="mb-1 block text-xs font-medium text-muted-foreground">Type</label>
          <UiInput v-model="form.type" placeholder="Meuble, Ordinateur, Écran…" />
        </div>

        <div>
          <label class="mb-1 block text-xs font-medium text-muted-foreground">Quantité</label>
          <input type="number" min="0" v-model.number="form.qty"
                 class="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" />
        </div>
        <div>
          <label class="mb-1 block text-xs font-medium text-muted-foreground">SKU / Référence</label>
          <UiInput v-model="form.sku" placeholder="Réf. interne" />
        </div>

        <div>
          <label class="mb-1 block text-xs font-medium text-muted-foreground">Projet</label>
          <UiInput v-model="form.project" placeholder="Projet / affectation" />
        </div>
        <div>
          <label class="mb-1 block text-xs font-medium text-muted-foreground">Fournisseur</label>
          <UiSelect v-model="form.company_id">
            <option :value="null">—</option>
            <option v-for="c in companies" :key="c.id" :value="c.id">{{ c.name }}</option>
          </UiSelect>
        </div>

        <div>
          <label class="mb-1 block text-xs font-medium text-muted-foreground">Fournisseur (texte libre)</label>
          <UiInput v-model="form.supplier_name" placeholder="Ex. Amazon, leboncoin…" />
        </div>
        <div>
          <label class="mb-1 block text-xs font-medium text-muted-foreground">N° de facture</label>
          <UiInput v-model="form.invoice_number" placeholder="Réf. facture fournisseur" />
        </div>

        <div>
          <label class="mb-1 block text-xs font-medium text-muted-foreground">Coût d'achat (total ligne)</label>
          <input type="number" step="0.01" v-model.number="form.cost_price"
                 class="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" />
        </div>
        <div>
          <label class="mb-1 block text-xs font-medium text-muted-foreground">Statut comptable</label>
          <UiSelect v-model="form.accounting_status">
            <option v-for="s in statuses" :key="s.id" :value="s.id">{{ s.label }}</option>
          </UiSelect>
        </div>

        <div>
          <label class="mb-1 block text-xs font-medium text-muted-foreground">Nombre vendu</label>
          <input type="number" min="0" v-model.number="form.sold_qty"
                 class="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" />
        </div>
        <div>
          <label class="mb-1 block text-xs font-medium text-muted-foreground">Montant des ventes (total)</label>
          <input type="number" step="0.01" v-model.number="form.sales_amount"
                 class="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" />
        </div>

        <div class="md:col-span-2">
          <label class="mb-1 block text-xs font-medium text-muted-foreground">Notes</label>
          <textarea v-model="form.notes" rows="2"
                    class="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring" />
        </div>
      </div>

      <div class="flex items-center justify-between rounded-md bg-muted/50 px-3 py-2 text-sm">
        <span class="text-muted-foreground">Stock restant</span>
        <span class="font-semibold">{{ stockRemaining }}</span>
      </div>

      <div v-if="form.invoice_id" class="text-xs text-muted-foreground">
        Rattaché à la facture <span class="font-mono">{{ String(form.invoice_id).substr(0, 10) }}</span>
      </div>

      <div class="flex justify-end gap-2">
        <UiButton variant="ghost" @click="$emit('close')">Annuler</UiButton>
        <UiButton variant="brand" :disabled="!form.name || !form.name.trim() || saving" @click="save">
          {{ item && item.id ? "Enregistrer" : "Créer" }}
        </UiButton>
      </div>
    </div>
  </UiDialog>
</template>

<script>
import UiDialog from "@/components/ui/UiDialog.vue";
import UiInput from "@/components/ui/UiInput.vue";
import UiSelect from "@/components/ui/UiSelect.vue";
import UiButton from "@/components/ui/UiButton.vue";
import { INVENTORY_STATUSES, useInventoryService } from "@/composables/inventoryService";

const EMPTY = {
  name: "",
  date: null,
  qty: 1,
  sku: "",
  type: "",
  project: "",
  company_id: null,
  supplier_name: "",
  invoice_id: null,
  invoice_number: "",
  cost_price: null,
  sold_qty: 0,
  sales_amount: 0,
  accounting_status: "en_stock",
  notes: "",
};

export default {
  name: "InventoryItemDialog",
  components: { UiDialog, UiInput, UiSelect, UiButton },
  props: {
    open: { type: Boolean, default: false },
    item: { type: Object, default: null },
    defaults: { type: Object, default: () => ({}) },
    companies: { type: Array, default: () => [] },
  },
  emits: ["close", "saved"],
  setup() {
    return { inventory: useInventoryService() };
  },
  data() {
    return { form: { ...EMPTY }, statuses: INVENTORY_STATUSES, saving: false };
  },
  computed: {
    stockRemaining() {
      return (Number(this.form.qty) || 0) - (Number(this.form.sold_qty) || 0);
    },
  },
  watch: {
    open(value) {
      if (value) this.reset();
    },
  },
  methods: {
    reset() {
      if (this.item && this.item.id) {
        this.form = { ...EMPTY, ...this.item };
      } else {
        this.form = { ...EMPTY, ...this.defaults };
      }
    },
    async save() {
      if (!this.form.name || !this.form.name.trim()) return;
      this.saving = true;
      try {
        const payload = { ...this.form };
        const saved = this.item && this.item.id
          ? await this.inventory.update(this.item.id, payload)
          : await this.inventory.create(payload);
        this.$emit("saved", saved);
        this.$emit("close");
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>
